import React from "react";
import { FileQuestion, Lock, Fingerprint } from "lucide-react";

/**
 * JerichoDossierCards — file-cabinet drawer slabs.
 * Each slab has: manila tab strip, corner rivets, classification stamp,
 * paperclip/clasp icon, embossed depth shadow. Asymmetric widths.
 */
export const JerichoDossierCards = ({ drawers }) => {
  return (
    <section data-testid="jericho-dossier-cards" className="relative">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20]">
            // DOSSIER VAULT
          </div>
          <div className="font-display text-[26px] sm:text-[30px] text-[#E4DFD3] leading-tight">
            Sealed&nbsp;Drawer&nbsp;Index
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A39E93] mt-1">
            Working material · not for public archive · authorized eyes only
          </div>
        </div>
        <div className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
          DSR-12 · 04 OF 07
        </div>
      </div>

      {/* Asymmetric layout: 1-2 cols mobile, 12-col grid desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4">
        {drawers.map((d, i) => {
          // Asymmetric column spans: 5 / 7 / 4 / 8 — gives editorial rhythm
          const spans = ["lg:col-span-5", "lg:col-span-7", "lg:col-span-4", "lg:col-span-8"];
          return (
            <Drawer key={d.tag} idx={i} {...d} span={spans[i % spans.length]} />
          );
        })}
      </div>
    </section>
  );
};

const Drawer = ({ tag, title, blurb, disabled = false, idx, span }) => {
  const code = `JCH-${String(101 + idx).padStart(3, "0")}`;
  const accents = [
    { stripe: "#D92D20", tab: "#CBB892", barColor: "#D92D20" },
    { stripe: "#E0A526", tab: "#E4DFD3", barColor: "#E0A526" },
    { stripe: "#5DD4B3", tab: "#CBB892", barColor: "#5DD4B3" },
    { stripe: "#7d6b53", tab: "#A39E93", barColor: "#7d6b53" },
  ];
  const a = accents[idx % accents.length];

  return (
    <div
      data-testid={`jericho-drawer-${tag.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative ${span} group`}
    >
      {/* Manila tab strip on top */}
      <div className="flex items-end gap-1 pl-3 -mb-px">
        <div
          className="h-3 px-2 border border-b-0 flex items-center font-mono text-[8.5px] uppercase tracking-[0.35em]"
          style={{
            background: "linear-gradient(to bottom, #1b1814, #100e0c)",
            borderColor: a.stripe + "60",
            color: a.tab,
          }}
        >
          {tag}
        </div>
        <div
          className="h-3 w-6 border border-b-0"
          style={{
            background: "linear-gradient(to bottom, #14110e, #0a0907)",
            borderColor: "#1f1c19",
          }}
        />
      </div>

      {/* The drawer body */}
      <div
        className={
          "relative border " +
          (disabled
            ? "border-[#2A2722]/80 opacity-80"
            : "border-[#2A2722] hover:border-[#D92D20]/60 transition-colors")
        }
        style={{
          background:
            "linear-gradient(155deg, #15110d 0%, #0c0a08 45%, #08060a 100%)",
          boxShadow:
            "0 30px 50px -20px rgba(0,0,0,0.85), inset 0 0 30px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.02)",
        }}
      >
        {/* Top warning stripe */}
        <div
          className="h-[3px] w-full"
          style={{
            background:
              `repeating-linear-gradient(45deg, ${a.barColor} 0 6px, #0a0907 6px 12px)`,
            opacity: 0.55,
          }}
        />

        {/* Corner rivets */}
        <Rivet pos="tl" />
        <Rivet pos="tr" />
        <Rivet pos="bl" />
        <Rivet pos="br" />

        {/* Embossed paperclip / fingerprint motif (right side) */}
        <div className="absolute top-2 right-3 opacity-30 group-hover:opacity-60 transition-opacity">
          <Fingerprint size={36} className="text-[#5a544a]" />
        </div>

        <div className="relative px-4 sm:px-5 py-4 sm:py-5">
          {/* Code + LEVEL */}
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.35em]" style={{ color: a.barColor }}>
              {code}
            </span>
            <span className={
              "font-mono text-[9px] uppercase tracking-[0.3em] " +
              (disabled ? "text-[#E0A526]" : "text-[#5DD4B3]")
            }>
              {disabled ? "LVL-04 · SEALED" : "LVL-02 · DRAFTING"}
            </span>
          </div>

          <div className="font-display text-[20px] sm:text-[22px] text-[#E4DFD3] leading-tight mb-2 max-w-[80%]">
            {title}
          </div>
          <p className="text-[12px] sm:text-[12.5px] text-[#A39E93] leading-relaxed max-w-[92%]">
            {blurb}
          </p>

          {/* Bottom strip: classification + watermark */}
          <div className="mt-4 pt-3 border-t border-[#2A2722] flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
              {disabled ? <Lock size={10} /> : <FileQuestion size={10} />}
              {disabled ? "SEALED" : "DRAFTING IN PROGRESS"}
            </span>
            <ClassificationStamp variant={disabled ? "sealed" : "internal"} />
          </div>
        </div>
      </div>

      {/* Drawer pull handle (bottom center) */}
      <div className="flex justify-center -mt-px">
        <div
          className="w-12 h-[5px] border border-t-0"
          style={{
            background: "linear-gradient(to bottom, #1b1814, #0a0907)",
            borderColor: "#2A2722",
          }}
        />
      </div>
    </div>
  );
};

const Rivet = ({ pos }) => {
  const map = {
    tl: "top-1.5 left-1.5",
    tr: "top-1.5 right-1.5",
    bl: "bottom-1.5 left-1.5",
    br: "bottom-1.5 right-1.5",
  };
  return (
    <span
      aria-hidden="true"
      className={`absolute ${map[pos]} block w-[7px] h-[7px] rounded-full`}
      style={{
        background:
          "radial-gradient(circle at 30% 30%, #5a4f44 0%, #1a1612 65%, #050403 100%)",
        boxShadow: "inset 0 0 1px rgba(0,0,0,0.9), 0 0 1px rgba(255,255,255,0.04)",
      }}
    />
  );
};

const ClassificationStamp = ({ variant }) => {
  if (variant === "sealed") {
    return (
      <span
        className="inline-block font-mono text-[8.5px] uppercase tracking-[0.4em] text-[#E0A526] border px-1.5 py-[1px] -rotate-2"
        style={{
          borderColor: "#E0A526",
          background: "rgba(224,165,38,0.06)",
          boxShadow: "inset 0 0 0 1px rgba(224,165,38,0.15)",
        }}
      >
        ⛛ DO NOT OPEN
      </span>
    );
  }
  return (
    <span
      className="inline-block font-mono text-[8.5px] uppercase tracking-[0.4em] text-[#D92D20] border px-1.5 py-[1px] -rotate-2"
      style={{
        borderColor: "#D92D20",
        background: "rgba(217,45,32,0.06)",
        boxShadow: "inset 0 0 0 1px rgba(217,45,32,0.18)",
      }}
    >
      INTERNAL ONLY
    </span>
  );
};

export default JerichoDossierCards;
